import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard, { FeaturedCard, TrendingRow, articles } from '@/components/base/ArticleCard';
import { categories, hotListIds, siteFaqs, siteName, siteSlogan, type Category } from '@/mocks/articles';
import SectionHeading from '@/components/base/SectionHeading';
import NewsletterForm from '@/components/feature/NewsletterForm';
import JsonLd from '@/components/base/JsonLd';
import { catName } from '@/components/base/ArticleCard';

const catIcon: Record<string, string> = {
  specs: 'ri-cpu-line',
  renders: 'ri-camera-lens-line',
  price: 'ri-price-tag-3-line',
  inside: 'ri-building-line',
  scene: 'ri-magic-line',
};

function CategoryStrip() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
      {categories.map((c: Category) => (
        <Link
          key={c.slug}
          to={`/news?cat=${c.slug}`}
          className="group flex items-start gap-3 rounded-2xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-200 hover:bg-accent-50"
        >
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700 transition-colors group-hover:bg-accent-500 group-hover:text-background-50">
            <i className={`${catIcon[c.slug] ?? 'ri-fire-line'} text-lg`} aria-hidden="true" />
          </span>
          <span className="flex flex-col">
            <span className="font-heading text-sm font-bold text-foreground-900 group-hover:text-accent-800">{c.name}</span>
            <span className="mt-0.5 text-xs leading-5 text-foreground-500">{c.brief}</span>
          </span>
        </Link>
      ))}
    </div>
  );
}

const testimonials = [
  {
    name: '阿泽（上海 · 数码爱好者）',
    text: '每天早晚各刷一次，参数曝光和谍照更新得又快又全。最关键是会标注「疑似/据传」，不会像某些账号一样把爆料说成定论。',
  },
  {
    name: '林同学（深圳 · 大三学生）',
    text: '买手机前我习惯先来这里看价格预测和上手解读。上一部手机就是按这里的判断等到降价入手，省了不少预算，很实用。',
  },
  {
    name: 'Momo（成都 · 自由职业）',
    text: '厂商内幕栏目里的供应链与立项分析很对味，信息有交叉验证，不是标题党。评论区氛围也理性，是我少数愿意认真读的爆料站。',
  },
];

function FaqPreview() {
  const [open, setOpen] = useState<number>(0);
  const preview = siteFaqs.slice(0, 4);
  return (
    <div className="divide-y divide-background-200 rounded-2xl border border-background-200 bg-background-50">
      {preview.map((item, i) => {
        const active = open === i;
        return (
          <div key={item.q}>
            <button
              type="button"
              onClick={() => setOpen(active ? -1 : i)}
              aria-expanded={active}
              className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left md:px-6 md:py-5"
            >
              <span className="flex items-start gap-3">
                <span className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${active ? 'bg-primary-500 text-background-50' : 'bg-secondary-100 text-secondary-800'}`}>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="font-heading text-sm font-bold text-foreground-900 md:text-base">{item.q}</span>
              </span>
              <i className={`mt-1 shrink-0 text-lg text-foreground-400 transition-transform ${active ? 'rotate-45 text-primary-600' : ''} ${active ? 'ri-subtract-line' : 'ri-add-line'}`} aria-hidden="true" />
            </button>
            {active && <div className="px-6 pb-5 pl-[3.25rem] text-sm leading-7 text-foreground-600 md:px-6 md:pl-[3.75rem]">{item.a}</div>}
          </div>
        );
      })}
    </div>
  );
}

export default function Home() {
  const featured = articles.find((a) => a.featured) ?? articles[0];
  const hot = hotListIds
    .map((id) => articles.find((a) => a.id === id))
    .filter((a): a is NonNullable<typeof a> => Boolean(a));
  const picks = articles.filter((a) => a.pick);
  const latest = articles.filter((a) => !a.featured).slice(0, 8);

  const webSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${siteName} - 首页`,
    description: '每日新料专注数码圈一手爆料，汇总手机参数曝光、未发布产品图、配置价格预测与厂商内部消息。',
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <Navbar />
      <main id="main">
        <JsonLd data={webSchema} />

        {/* 首屏品牌介绍 */}
        <section className="relative overflow-hidden bg-background-50">
          <div className="mx-auto w-full max-w-7xl px-4 pb-12 pt-12 md:px-6 md:pb-16 md:pt-16">
            <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:gap-14">
              <div className="max-w-2xl flex-1">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-800">
                  <i className="ri-flashlight-fill text-sm" aria-hidden="true" />
                  今日数码爆料 · 持续更新
                </div>
                <h1 className="mt-5 font-heading text-4xl font-black leading-[1.15] tracking-tight text-foreground-950 md:text-[3.25rem]">
                  {siteName}
                  <span className="mt-2 block text-foreground-900">{siteSlogan}</span>
                </h1>
                <p className="mt-5 max-w-xl text-base leading-7 text-foreground-600 md:text-lg md:leading-8">
                  我们专职盯紧手机圈的一手消息：手机参数曝光、未发布产品图、配置价格预测，以及厂商内部消息。想比媒体更早看到新机真容、提前锁定定价区间？这里都有。
                </p>
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <Link
                    to="/news"
                    className="inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    浏览最新爆料
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                  <a
                    href="#subscribe"
                    className="inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-mail-send-line" aria-hidden="true" />
                    订阅每日更新
                  </a>
                </div>
                <dl className="mt-8 grid max-w-md grid-cols-3 gap-3 border-t border-background-200 pt-6">
                  {[
                    { n: '1200+', l: '已收录爆料' },
                    { n: '24h', l: '全年不断更' },
                    { n: '多源', l: '交叉信源验证' },
                  ].map((s) => (
                    <div key={s.l}>
                      <dt className="sr-only">{s.l}</dt>
                      <dd className="font-heading text-2xl font-black text-accent-700">{s.n}</dd>
                      <dd className="mt-1 text-xs font-medium text-foreground-500">{s.l}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              {/* 首屏视觉 */}
              <div className="relative flex-1">
                <div className="overflow-hidden rounded-3xl border border-background-200 shadow-soft">
                  <img
                    src={featured.image}
                    alt={featured.imageAlt}
                    className="h-full w-full object-cover object-top"
                    width={1600}
                    height={1000}
                  />
                </div>
                <div className="absolute -left-4 bottom-6 hidden w-52 rounded-2xl border border-background-200 bg-background-50/95 p-4 shadow-soft backdrop-blur lg:block">
                  <p className="flex items-center gap-2 text-xs font-bold text-foreground-800">
                    <span className="inline-flex h-2 w-2 rounded-full bg-primary-500" aria-hidden="true" />
                    焦点 · {catName(featured.category)}
                  </p>
                  <p className="mt-2 font-heading text-sm font-bold leading-snug text-foreground-900">{featured.title}</p>
                  <Link to={`/news/${featured.id}`} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:text-primary-600">
                    立即查看 <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 焦点爆料 + 热榜 */}
        <section className="bg-background-100/70 py-12 md:py-16">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <SectionHeading
              eyebrow="聚焦爆料"
              title="今日最值得关注的手机消息"
              desc="从参数到谍照、从定价到内幕，编辑部为你挑出今天最有看点的几条。"
              actionTo="/news"
              actionLabel="查看全部爆料"
            />
            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <FeaturedCard article={featured} />
              </div>
              <aside className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-4 md:p-5" aria-label="今日热榜">
                <h3 className="flex items-center gap-2 font-heading text-base font-black text-foreground-900">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-accent-500 text-background-50">
                    <i className="ri-fire-line" aria-hidden="true" />
                  </span>
                  今日热榜 TOP 5
                </h3>
                <p className="mt-1 text-xs text-foreground-500">按近 24 小时阅读与讨论热度排序</p>
                <div className="mt-2 divide-y divide-background-100">
                  <TrendingRow articles={hot} />
                </div>
                <Link to="/news" className="mt-2 inline-flex items-center gap-1 border-t border-background-200 pt-3 text-sm font-semibold text-primary-700 hover:text-primary-600">
                  查看更多热榜内容 <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </aside>
            </div>
          </div>
        </section>

        {/* 栏目分类 */}
        <section className="bg-background-50 py-12 md:py-16">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <SectionHeading eyebrow="栏目导航" title="按你的兴趣直达栏目" desc="五类栏目分工明确：参数、谍照、定价、内幕与解读，一触即达。" />
            <div className="mt-8">
              <CategoryStrip />
            </div>
          </div>
        </section>

        {/* 编辑精选 */}
        <section className="bg-accent-50/60 py-12 md:py-16">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <SectionHeading
              eyebrow="编辑精选"
              title="值得花几分钟细读的选题"
              desc="编辑部成员亲手挑选，信息量大、判断扎实，值得放进口袋慢慢看。"
              actionTo="/news"
              actionLabel="更多深度内容"
            />
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
              {picks.map((a) => (
                <article key={a.id} className="group overflow-hidden rounded-2xl border border-accent-100 bg-background-50 transition-colors hover:border-accent-300">
                  <Link to={`/news/${a.id}`} className="flex h-full flex-col sm:flex-row">
                    <div className="relative aspect-[16/10] w-full overflow-hidden sm:aspect-auto sm:w-2/5">
                      <img src={a.image} alt={a.imageAlt} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <span className="inline-flex w-fit items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-800">
                        <i className="ri-vip-crown-2-line" aria-hidden="true" />
                        {catName(a.category)}
                      </span>
                      <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700 md:text-lg">
                        {a.title}
                      </h3>
                      <p className="mt-2 line-clamp-2 text-sm leading-6 text-foreground-600">{a.summary}</p>
                      <p className="mt-auto pt-3 text-xs text-foreground-500">{a.date} · {a.readTime} · {a.readCount} 阅读</p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* 最新爆料 */}
        <section className="bg-background-50 py-12 md:py-16">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <SectionHeading eyebrow="最新更新" title="最新数码爆料速递" desc="按发布时间滚动更新的参数曝光、新机谍照与价格预测。" actionTo="/news" actionLabel="进入资讯中心" />
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {latest.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link
                to="/news"
                className="inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-6 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-500 hover:text-background-50"
              >
                加载更多爆料
                <i className="ri-arrow-down-line" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* 订阅 */}
        <section id="subscribe" className="bg-background-100 py-12 md:py-16">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <div className="rounded-3xl border border-primary-100 bg-gradient-to-br from-primary-50 via-background-50 to-accent-50 p-8 md:p-12">
              <div className="mx-auto max-w-2xl text-center">
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
                  <i className="ri-mail-send-line" aria-hidden="true" />
                  每日电子报
                </span>
                <h2 className="mt-5 font-heading text-2xl font-black leading-tight text-foreground-900 md:text-3xl">
                  把每天的爆料，装进你的收件箱
                </h2>
                <p className="mt-3 text-sm leading-6 text-foreground-600 md:text-base">
                  订阅 {siteName} 电子报，第一时间收到参数曝光、新机谍照与价格预测摘要。不打扰、可随时退订。
                </p>
                <div className="mt-7">
                  <NewsletterForm id="home-newsletter" />
                </div>
                <p className="mt-5 text-xs text-foreground-400">已订阅的读者中，超过 70% 每周至少点开一次推送。</p>
              </div>
            </div>
          </div>
        </section>

        {/* 用户口碑 */}
        <section className="bg-background-50 py-12 md:py-16">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
            <SectionHeading eyebrow="读者口碑" title="看看爆友们怎么说" desc="理性、克制、有态度，是读者对我们的最高评价。" />
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {testimonials.map((t) => (
                <figure key={t.name} className="flex flex-col rounded-2xl border border-background-200 bg-background-100 p-6">
                  <i className="ri-chat-1-fill text-2xl text-accent-500" aria-hidden="true" />
                  <blockquote className="mt-4 flex-1 text-sm leading-7 text-foreground-700">“{t.text}”</blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                      {t.name.charAt(0)}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="flex items-center gap-0.5 text-xs text-accent-600" aria-label="五星好评">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i key={i} className="ri-star-fill" aria-hidden="true" />
                        ))}
                      </p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* 常见问题预览 */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="mx-auto w-full max-w-4xl px-4 md:px-6">
            <SectionHeading center eyebrow="读者关心的问题" title="关于每日新料，你可能想知道" desc="从信源可信度到如何及时获取更新，常见疑问集中解答。" />
            <div className="mt-8">
              <FaqPreview />
            </div>
            <div className="mt-6 text-center">
              <Link to="/faq" className="inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-6 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700">
                查看全部常见问题
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}